import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { addDays, startOfDay } from "date-fns";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { DateRangePicker } from "@/components/ui/date-range-picker";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ChevronsUpDown, RefreshCw, Calendar, Users } from "lucide-react";
import { usePersonasQueryNoPublic } from "@/api/personas";
import { userCreditQueries } from "@/api/user-credits";
import { useToast } from "@/hooks/use-toast";
import { OverviewTab } from "@/components/account/user-analytics/OverviewTab";
import { UsageTab } from "@/components/account/user-analytics/UsageTab";
import { TransactionsTab } from "@/components/account/user-analytics/TransactionsTab";
import { DateRange } from "@/lib/date";
import { useSelectedPersona } from "@/hooks/use-selected-persona";

const TIME_PRESETS = [
  { label: "Last 7 Days", days: 7 },
  { label: "Last 30 Days", days: 30 },
  { label: "Last 3 Months", days: 90 },
  { label: "Last 6 Months", days: 180 },
  { label: "Year to Date", days: 365 },
];

export function UserAnalytics() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  // State management
  const [userOpen, setUserOpen] = useState(false);
  const [selectedPersonaId, setSelectedPersonaId] = useSelectedPersona();
  const [activeTab, setActiveTab] = useState("overview");
  const [dateRange, setDateRange] = useState<DateRange>({
    from: addDays(new Date(), -30),
    to: new Date(),
  });
  const [selectedTimePreset, setSelectedTimePreset] = useState("30");

  // Queries
  const { data: allPersonas = [] } = usePersonasQueryNoPublic();

  const { isLoading: isHistoryLoading } = useQuery({
    ...userCreditQueries.history(
      selectedPersonaId ?? "",
      dateRange.from.toISOString(),
      dateRange.to.toISOString(),
    ),
    enabled: !!selectedPersonaId,
  });

  // Derived data
  const selectedUser = allPersonas.find(
    (p) => p.persona_id === selectedPersonaId,
  );

  // Handlers
  const handleUserSelect = (persona_id: string) => {
    const selectedUser = allPersonas.find((p) => p.persona_id === persona_id);
    if (selectedUser) {
      setSelectedPersonaId(persona_id);
      setUserOpen(false);
      setActiveTab("overview");
    }
  };

  const handleTimePresetSelect = (days: number) => {
    setSelectedTimePreset(days.toString());
    setDateRange({
      from: addDays(new Date(), -days),
      to: new Date(),
    });
  };

  const handleRefreshData = () => {
    if (selectedPersonaId) {
      toast({
        title: "Refreshing data",
        description: "Please wait while we fetch the latest information.",
      });
      queryClient.invalidateQueries(userCreditQueries.all(selectedPersonaId));
    }
  };

  return (
    <div className="space-y-6">
      <Card className="p-6">
        <CardHeader className="px-0 pt-0">
          <div className="flex justify-between items-center">
            <div>
              <CardTitle className="text-2xl font-semibold text-primary">
                Credit Usage &amp; Analytics
              </CardTitle>
              <CardDescription>
                Comprehensive overview of user credit usage and transaction
                history
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="icon"
                onClick={handleRefreshData}
                disabled={!selectedPersonaId || isHistoryLoading}
              >
                <RefreshCw
                  className={`h-4 w-4 ${isHistoryLoading ? "animate-spin" : ""}`}
                />
              </Button>
            </div>
          </div>
        </CardHeader>

        <CardContent className="px-0">
          {/* User Selection & Filters */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
            {/* User Selection */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Users className="h-4 w-4" />
                Select Agent
              </Label>
              <Popover open={userOpen} onOpenChange={setUserOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={userOpen}
                    className="w-full justify-between rounded-lg"
                  >
                    {selectedUser ? (
                      <div className="w-full flex justify-between items-center gap-2">
                        <div className="w-full flex items-center gap-2">
                          <span className="  truncate">
                            {selectedUser.name}
                          </span>

                          <Badge variant="secondary">
                            {
                              // @ts-expect-error role probably does not exist on user
                              selectedUser.role ?? "Agent"
                            }
                          </Badge>
                        </div>
                        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                      </div>
                    ) : (
                      <>
                        <span>Select Agent...</span>
                        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                      </>
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[400px] p-0">
                  <Command>
                    <CommandInput placeholder="Search agents..." />
                    <CommandList>
                      <CommandGroup>
                        {allPersonas
                          .sort((a, b) => a.name.localeCompare(b.name))
                          .map((persona) => (
                            <CommandItem
                              key={persona.persona_id}
                              value={persona.persona_id}
                              onSelect={handleUserSelect}
                              keywords={[persona.name]}
                            >
                              <div className="flex items-center justify-between w-full">
                                <span className="truncate">
                                  {persona.name || persona.persona_id}
                                </span>
                                <div className="flex items-center gap-2">
                                  <Badge variant="outline">
                                    {persona.qstarlabs_tenant || "No Tenant"}
                                  </Badge>
                                </div>
                              </div>
                            </CommandItem>
                          ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>

            {/* Time Presets */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                Time Period
              </Label>
              <Select
                value={selectedTimePreset}
                onValueChange={(value) => handleTimePresetSelect(Number(value))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select time period" />
                </SelectTrigger>
                <SelectContent>
                  {TIME_PRESETS.map((preset) => (
                    <SelectItem
                      key={preset.days}
                      value={preset.days.toString()}
                    >
                      {preset.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Custom Date Range */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                Custom Range
              </Label>
              <DateRangePicker
                value={dateRange}
                onChange={(range) => {
                  if (range?.from && range?.to) {
                    setDateRange({
                      from: startOfDay(range.from),
                      to: startOfDay(range.to),
                    });
                  }
                }}
              />
            </div>
          </div>

          {selectedUser ? (
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="mb-4">
                <TabsTrigger value="overview">Overview</TabsTrigger>
                <TabsTrigger value="usage">Usage Analysis</TabsTrigger>
                <TabsTrigger value="transactions">Transactions</TabsTrigger>
              </TabsList>

              <TabsContent value="overview">
                <OverviewTab user={selectedUser} dateRange={dateRange} />
              </TabsContent>

              <TabsContent value="usage">
                <UsageTab
                  username={selectedUser.persona_id}
                  dateRange={dateRange}
                />
              </TabsContent>

              <TabsContent value="transactions">
                <TransactionsTab username={selectedUser.persona_id} />
              </TabsContent>
            </Tabs>
          ) : (
            <div className="text-center py-12 text-muted-foreground">
              <Users className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <h3 className="text-lg font-semibold mb-2">No Agent Selected</h3>
              <p>
                Select an agent to view their credit usage and transaction
                history.
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
